/**
 * Responsibility: how complete the card index is, and what is still owed.
 *
 * **A different question from the Data Hub's.** That tab is the research
 * corpus - photographs harvested to calibrate a grade. This is the
 * catalogue the search box answers from, filled by a crawler on a timer.
 *
 * **It is never finished, and the page says so rather than implying it.**
 * Once every set has been read the oldest reading becomes the next one due,
 * so "complete" is not a state this reaches; keeping up is. That is why
 * *owed* is a headline figure beside *read* - a catalogue can be fully
 * crawled and a month out of date, and one percentage would call that fine.
 *
 * **Three separate jobs, three separate bars.** Sets read by the crawler,
 * cards those sets produced, and pictures hashed by the harvest so a scan
 * can match them. They advance at different speeds and a single number
 * would hide which one was stuck.
 *
 * **What it costs and whether it is safe are on the same screen**, because
 * they are the two things that turn into an outage: the volume filling up,
 * and a week of crawling that nothing holds a copy of.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery } from '@tanstack/react-query';
import { MetricCard } from '@kit/components';
import { hubApi } from '../../api/client';
import { IndexCatalogueTable } from './IndexCatalogueTable';
import { StorageIcons } from './StorageIcons';
import { readableBytes, stamp } from './dataHubFormat';

/** A share as a bar with its figure, used for each of the three jobs. */
function Progress({
  label,
  done,
  total,
  note,
}: {
  label: string;
  done: number;
  total: number | null;
  note: string;
}) {
  const share = total ? Math.min(done / total, 1) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {total === null
            ? `${done.toLocaleString()} — no total yet`
            : `${done.toLocaleString()} / ${total.toLocaleString()} (${(share * 100).toFixed(1)}%)`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        // Always visible: a genuine early share is a fraction of a percent,
        // and an empty bar reads as broken rather than as just started.
        value={Math.max(share * 100, 0.5)}
        color={share >= 0.99 ? 'success' : 'primary'}
        sx={{ height: 8, borderRadius: 1 }}
      />
      <Typography variant="caption" color="text.secondary">
        {note}
      </Typography>
    </Box>
  );
}

export function IndexCard() {
  const status = useQuery({ queryKey: ['admin', 'index'], queryFn: hubApi.indexStatus });
  const data = status.data;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Card index</Typography>
              <Typography variant="body2" color="text.secondary">
                What the search box answers from. A crawler reads a few sets every
                two minutes, so this is never finished by design - once everything
                has been read, the oldest reading is the next one due.
              </Typography>
            </Box>
            <Tooltip title="Read it again">
              <span>
                <IconButton
                  size="small"
                  disabled={status.isFetching}
                  onClick={() => void status.refetch()}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />

          {status.isLoading ? <LinearProgress /> : null}
          {status.isError ? (
            <Alert severity="error">
              The index could not be read. The analysis service may be offline.
            </Alert>
          ) : null}

          {data ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                }}
              >
                <MetricCard
                  label="Cards indexed"
                  value={data.cards_indexed === null ? '—' : data.cards_indexed.toLocaleString()}
                  helper={data.cards_indexed === null ? 'still building' : 'searchable now'}
                />
                <MetricCard
                  label="Sets read"
                  value={data.sets_read.toLocaleString()}
                  helper={`of ${data.sets_known.toLocaleString()} listed`}
                />
                <MetricCard
                  label="Owed"
                  value={data.sets_due.toLocaleString()}
                  helper="never read, or gone stale"
                />
                <MetricCard
                  label="On disk"
                  value={readableBytes(data.cache_bytes + data.scan.thumbnail_bytes)}
                  helper={`${readableBytes(data.cache_bytes)} index, ${readableBytes(
                    data.scan.thumbnail_bytes,
                  )} pictures`}
                />
              </Box>

              {data.cards_indexed === null ? (
                <Alert severity="info">
                  The index is still being built. It rebuilds on a background thread
                  after a restart, which takes a minute or so - the card counts
                  appear when it finishes.
                </Alert>
              ) : null}

              {/* Where it all lives, in the same two symbols the rows and
                  the Data Hub use. The cloud is read from the receipt the
                  backup run writes, so it says an upload happened rather
                  than that one is scheduled. */}
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <StorageIcons
                  onServer
                  backedUp={Boolean(data.backed_up_at)}
                  serverNote="The catalogue caches and the hash database are on the Oracle box."
                  cloudNote={
                    data.backed_up_at
                      ? `Last archive ${stamp(data.backed_up_at)}.`
                      : 'No backup has completed yet, so nothing here is archived.'
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  {data.backed_up_at
                    ? `Last backed up ${stamp(data.backed_up_at)}. ${data.scan.thumbnails_backed_up.toLocaleString()} thumbnails sent; the hash database ${
                        data.scan.hashes_backed_up ? 'rode the last archive' : 'has not been archived yet'
                      }.`
                    : 'No backup has completed yet. Nothing on this tab is archived.'}
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Progress
                  label="Sets crawled"
                  done={data.sets_read}
                  total={data.sets_known || null}
                  note="Every set read at least once. The crawl then keeps re-reading the oldest."
                />
                <Progress
                  label="Pictures hashed"
                  done={data.scan.hashed + data.scan.without_image}
                  total={data.scan.target}
                  note={
                    data.scan.without_image > 0
                      ? `${data.scan.without_image.toLocaleString()} had no usable picture and are counted as done - they cannot be hashed however long it runs. ${readableBytes(
                          data.scan.thumbnail_bytes,
                        )} of thumbnails stored.`
                      : `${readableBytes(data.scan.thumbnail_bytes)} of thumbnails stored.`
                  }
                />
              </Stack>
            </>
          ) : null}
        </Stack>
      </Paper>

      {data ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">By catalogue</Typography>
              <Typography variant="body2" color="text.secondary">
                Daily catalogues first - Pokemon, baseball, basketball, football and
                soccer are read every day and ahead of everything else. The rest
                share whatever capacity is left, oldest first, so the long tail
                levels out instead of clumping.
              </Typography>
            </Box>
            <Divider />
            <IndexCatalogueTable catalogues={data.catalogues} />
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
